import React, { useEffect, useState } from "react";
import { Box, FormControl, MenuItem, Select, Autocomplete, TextField, IconButton, InputAdornment, List, ListItem, ListItemText, Checkbox, Typography, Button, Paper, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import { DateTime } from "luxon";
import { convertE164ToReadable, convertToCallablePhoneNumber, formatIsoToCustomDateStringWithEEEE } from "../utils/utils";
import { filterAppointmentTypesForPicker } from "../utils/appointmentTypes";
import { consolidateAvailableDates } from "../utils/availableDates";
import { AppointmentCategory, AppointmentType, AvailableDate, EHR, OmniPatient, Patient, RawWebptPatient, WaitlistPatient, WebptPatient } from "../types";
import { HelpTooltip } from "../subcomponents/HelpTooltip";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import { Colors } from "../Colors";
import { createPatient as createPatientThunk } from "../slices/PatientSlice";
import { dropdownStyles, menuPropsStyles } from "../styles/GeneralStyles";
import { HeaderBox, FooterBox } from "../subcomponents/SidebarComponents";
import CustomDatePicker from "../components/date-picker/DatePicker";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { first } from "lodash";
import { useSnackbar } from "../providers/SnackbarProvider";
import CustomAutocomplete from "../components/CustomAutocomplete";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Warning } from "postcss";
import DatePickerField from "../subcomponents/DatePickerField";
import { getAssignedProvider } from "../services/webptPatientService";
import { shallowEqual } from "react-redux";
import { fetchWebptPatients } from "../slices/WebptIntegrationSlice";
import { StyledTextField } from "../components/text-fields/StyledTextField";

//const timezones = moment.tz.names();
const timezones = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

const timezoneOptions = timezones.map((tz) => {
  const dt = DateTime.now().setZone(tz);
  const offsetString = dt.toFormat("ZZ");
  const text = `${tz} (UTC${offsetString})`;

  return { value: tz, label: text, offset: dt.offset };
});

timezoneOptions.sort((a, b) => a.offset - b.offset);

const contactOptions = [
  { value: "call", label: "Call" },
  { value: "text", label: "Text" },
];

const languageOptions = [
  { label: "English", value: "en" },
  { label: "Spanish", value: "es" },
];

// type AddPatientModalProps = {
//   open: boolean;
//   onClose: () => void;
// };

interface SettingsNumberInputProps {
  disabled: boolean;
  value: number | null;
  setter: (value: number | null) => void;
  label?: string;
}

const SettingsNumberInputLarge: React.FC<SettingsNumberInputProps> = ({ value, setter, label }) => {
  return (
    <Typography
      variant="body1"
      fontSize={"1rem"}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "20px",
      }}
    >
      {label}:
      <TextField
        type="number"
        value={value}
        onChange={(e) => setter(parseInt(e.target.value))}
        variant="outlined"
        sx={{
          marginLeft: "8px",
          "& .MuiInputBase-input": {
            color: "#FFF",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            background: Colors.textfield,
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 15px",
          },
        }}
      />
    </Typography>
  );
};

interface WebptAddPatientModalProps {
  onClick: () => void;
  onClose: () => void;
}

export const WebptAddPatientModal: React.FC<WebptAddPatientModalProps> = ({ onClick, onClose }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);
  const webptPatients = Object.values(useSelector((state: RootState) => state.webptIntegration.patientMap));
  const existingPatients = useSelector((state: RootState) => state.patients.patients);
  const { agentId } = useParams<{ agentId: string }>();
  const agent = useSelector((state: RootState) => state.agents.agents[0]);
  const [timezone, setTimezone] = React.useState<string>(organization?.timezone || DateTime.local().zoneName);
  const [availableDates, setAvailableDates] = React.useState<AvailableDate[]>([]);
  const [selectedWebptPatient, setSelectedWebptPatient] = React.useState<RawWebptPatient | undefined>(undefined);
  const [selectedProviders, setSelectedProviders] = React.useState<string[]>([]);
  const [providerGroupId, setProviderGroupId] = React.useState<string | undefined>(undefined);
  enum WebptPhoneType {
    Home = "PRN",
    Work = "WPN",
    Mobile = "PRS",
    Emergency = "EMR",
    AnsweringService = "ASN",
    NextOfKin = "NOK",
    PowerOfAttorney = "POA",
    Other = "OTR",
    Email = "NET",
  }
  const webptPhoneTypeStrings = {
    [WebptPhoneType.Home]: "Home",
    [WebptPhoneType.Work]: "Work",
    [WebptPhoneType.Mobile]: "Mobile",
    [WebptPhoneType.Emergency]: "Emergency",
    [WebptPhoneType.AnsweringService]: "Answering Service",
    [WebptPhoneType.NextOfKin]: "Next of Kin",
    [WebptPhoneType.PowerOfAttorney]: "Power of Attorney",
    [WebptPhoneType.Other]: "Other",
    [WebptPhoneType.Email]: "Email",
  };

  const [phoneNumber, setPhoneNumber] = React.useState<string>("");
  const [phoneNumbers, setPhoneNumbers] = React.useState<{ number: string; twilioType: string; type: WebptPhoneType; isPrimary: boolean }[]>([]);
  const [textEnabled, setTextEnabled] = React.useState<boolean>(true);
  const [callEnabled, setCallEnabled] = React.useState<boolean>(false);
  const [language, setLanguage] = React.useState<string>("en");
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const duration = agent?.defaultAppointmentDuration || 30;
  const [caseStudies, setCaseStudies] = useState<{ id: number; name: string; isActive: boolean }[]>([]); // Store case studies for the selected patient
  const [selectedCaseStudy, setSelectedCaseStudy] = React.useState<number | undefined>(undefined);
  const [maxAppointmentsPerWeek, setMaxAppointmentsPerWeek] = React.useState<number | null>(null);
  const [gapBetweenAppointments, setGapBetweenAppointments] = React.useState<number | null>(null);
  const providers = useSelector((state: RootState) => state.webptIntegration.providers);
  const providerOptions = providers.map((provider) => ({
    value: provider.id,
    label: provider.name,
  }));

  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);
  const providerGroupOptions = providerGroups.map((group) => ({
    value: group.providerGroupId,
    label: group.name,
  }));

  const [preferredName, setPreferredName] = useState<string | undefined>(undefined);

  const [contactMode, setContactMode] = React.useState<"text" | "call" | "both" | "">("text");

  const patientSearches = useSelector((state: RootState) => state.webptIntegration.patientSearches);
  const validPatientSearches = useSelector((state: RootState) => state.webptIntegration.validPatientSearches);
  const invalidPatientSearches = useSelector((state: RootState) => state.webptIntegration.invalidPatientSearches);
  const loadingPatients = useSelector((state: RootState) => state.webptIntegration.loadingPatients);
  const [currentQuery, setCurrentQuery] = useState<string>("");
  const currentQueryIsInvalid = invalidPatientSearches.includes(currentQuery);

  const [patientGroupId, setPatientGroupId] = React.useState<string | undefined>(undefined);
  const patientGroupOptions = [
    { value: "", label: "None" },
    ...(organization?.patientGroups?.map((group) => ({
      value: group.patientGroupId,
      label: group.name,
    })) || []),
  ];
  const [mode, setMode] = useState<"smart" | "legacy">("smart");
  const [selectedNextStep, setSelectedNextStep] = React.useState<"waitlist" | "planOfCare" | "examLink" | null>("examLink");

  const handleSwitchToSmart = () => {
    setMode("smart");
    if (selectedNextStep === "waitlist") {
      setSelectedNextStep("examLink");
    }
  };

  const handleSwitchToLegacy = () => {
    setMode("legacy");
    setSelectedNextStep("waitlist");
  };

  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<string | undefined>(undefined);
  const [initialEvalAppointmentTypeId, setInitialEvalAppointmentTypeId] = useState<string | undefined>(undefined);

  const navigate = useNavigate();

  const { showMessage } = useSnackbar();

  const dispatch = useAppDispatch();

  const handleInputChange = (value: string) => {
    setCurrentQuery(value);
  };

  const handleSearch = () => {
    const notSearched = !patientSearches.includes(currentQuery);
    const notSubsearched = !validPatientSearches.some((search) => currentQuery.includes(search));
    if (user?.token && notSearched && notSubsearched) {
      dispatch(fetchWebptPatients({ token: user.token, keywords: currentQuery }));
    }
  };

  const handleContactModeChange = (mode: "text" | "call" | "both") => {
    setContactMode(mode);
    setTextEnabled(mode === "text" || mode === "both");
    setCallEnabled(mode === "call" || mode === "both");
  };

  const handleProviderGroupChange = (providerGroupId: string) => {
    setProviderGroupId(providerGroupId);
    if (providerGroupId) {
      const group = providerGroups?.find((group) => group.providerGroupId === providerGroupId);
      if (group) {
        setSelectedProviders(group.providerIds);
      }
    }
  };

  useEffect(() => {
    if (gapBetweenAppointments && gapBetweenAppointments > 2) {
      setGapBetweenAppointments(2);
    } else if (gapBetweenAppointments && gapBetweenAppointments < 0) {
      setGapBetweenAppointments(0);
    }
  }, [gapBetweenAppointments]);

  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      if (!user?.token || !organization?.organizationId) {
        return;
      }
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            organizationId: organization.organizationId,
          },
        });
        setAppointmentTypes(response.data);
      } catch (error) {
        console.error("Error fetching appointment types:", error);
      }
    };
    fetchAppointmentTypes();
  }, [dispatch, user?.token, organization?.organizationId]);

  const createPatient = async () => {
    if (!user?.token || !organization?.organizationId) {
      return;
    }
    if (!selectedWebptPatient || !selectedCaseStudy) {
      return;
    }
    setLoading(true);

    try {
      const patientId = uuidv4();
      const organizationId = organization.organizationId;

      // example: 'Test, Sohan "First" - 01/01/2001 - (Default)'
      const study = caseStudies.find((study) => study.id === selectedCaseStudy);

      const patient: Patient = {
        patientId,
        organizationId,
        firstName: selectedWebptPatient.firstName,
        lastName: selectedWebptPatient.lastName,
        preferredName: preferredName,
        phoneNumber: convertToCallablePhoneNumber(phoneNumber),
        timezone: timezone,
        language: language,
        callEnabled: callEnabled,
        textEnabled: textEnabled,
        dob: selectedWebptPatient.dob,
        consentHistory: [],
      };

      const waitlistPatient: WaitlistPatient = {
        patientId,
        organizationId,
        priority: 1,
        availableDates: consolidateAvailableDates(availableDates),
        patientGroupId: patientGroupId || undefined,
        maxAppointmentsPerWeek: maxAppointmentsPerWeek === null ? undefined : maxAppointmentsPerWeek,
        gapBetweenAppointments: gapBetweenAppointments === null ? undefined : gapBetweenAppointments,
      };

      const providersToUse = selectedProviders.length > 0 ? selectedProviders : providerOptions.map((option) => option.value);

      const webptPatient: WebptPatient = {
        patientId,
        organizationId,
        webptPatientId: selectedWebptPatient.webptPatientId,
        webptProviderIds: providersToUse,
        providerGroupId: providerGroupId,
        webptAppointmentDuration: duration,
        webptCaseStudyId: selectedCaseStudy,
        webptAppointmentTypeId: selectedAppointmentType,
        initialEvalAppointmentTypeId: selectedNextStep === "examLink" ? initialEvalAppointmentTypeId || undefined : undefined,
      };

      const omniPatient: OmniPatient = {
        patientId: patientId,
        patient: patient,
        waitlist: waitlistPatient,
        [EHR.webpt]: webptPatient,
      };

      const resultAction = await dispatch(
        createPatientThunk({
          token: user.token,
          omniPatient,
        }),
      );
      if (createPatientThunk.rejected.match(resultAction)) {
        throw new Error(resultAction.payload?.message || "Failed to create patient");
      }
      if (createPatientThunk.fulfilled.match(resultAction) && resultAction.payload?.patientId && selectedNextStep === "examLink") {
        if (user?.token) {
          axios
            .post(
              `${process.env.REACT_APP_BACKEND_URL}/api/bookings/initial-visit/send`,
              {
                patientId: resultAction.payload.patientId,
              },
              {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                },
              },
            )
            .then(() => {
              showMessage("Successfully sent link to book initial visit", "success");
            })
            .catch(() => {
              showMessage("Error sending link to book initial visit", "error");
            });
        } else {
          showMessage("Error sending link to book initial visit", "error");
        }
      } else if (createPatientThunk.fulfilled.match(resultAction) && resultAction.payload?.patientId && selectedNextStep === "planOfCare") {
        navigate(`/dashboard/patients/${resultAction.payload.patientId}/plan-of-care`);
      }
      onClick();
      onClose();
      setSelectedWebptPatient(undefined);
      setAvailableDates([]);
    } catch (error: any) {
      // Handle any errors from the thunk if needed
      console.error("Error creating patient:", error);
      showMessage("Error creating patient: " + error.message, "error");
    }
    setLoading(false);
  };

  const webptPatientOptions = webptPatients.map((patient: RawWebptPatient) => ({
    value: patient.webptPatientId,
    label: `${patient.firstName} ${patient.lastName} (ID: ${patient.webptPatientId})`,
  }));

  const handleWebptPatientSelection = async (patientId: number) => {
    if (patientId) {
      const patient = webptPatients.find((patient) => patient.webptPatientId === patientId);
      setSelectedWebptPatient(patient);
      setShowPatientDetails(true);
      setPhoneNumber("");
      setPhoneNumbers([]);
      if (user?.token) {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/webpt/patients/${patientId}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            firstName: patient?.firstName,
            lastName: patient?.lastName,
          },
        });
        const data = response.data;

        const phoneNumber = data.phoneNumber;
        setPhoneNumber(phoneNumber ? convertToCallablePhoneNumber(phoneNumber) : "Unknown Phone Number");
        setPhoneNumbers(data.phoneNumbers);

        const studies = data.caseStudies;
        setCaseStudies(studies);

        if (studies.length === 1) {
          setSelectedCaseStudy(studies[0].id);
        } else {
          setSelectedCaseStudy(undefined);
        }

        // const provider = providers.find(
        //   (provider) => provider.name === patient?.providerName
        // );
        // setSelectedProviders(provider ? [provider.id] : []);

        if (!superOrganization?.ignorePreferredName) {
          setPreferredName(data.preferredName || undefined);
        }
      }
    } else {
      setSelectedWebptPatient(undefined);
      setSelectedCaseStudy(undefined);
      setCaseStudies([]);
      setPhoneNumber("");
      setPhoneNumbers([]);
      setShowPatientDetails(false);
      setSelectedProviders([]);
      setPreferredName(undefined);
    }
  };

  const handleCloseDetails = () => {
    setSelectedWebptPatient(undefined);
    setSelectedCaseStudy(undefined);
    setCaseStudies([]);
    setPhoneNumber("");
    setPhoneNumbers([]);
    setShowPatientDetails(false);
    setSelectedProviders([]);
    setPreferredName(undefined);
  };

  const handleRemoveDate = (index: number) => {
    setAvailableDates((prevDates) => prevDates.filter((_, idx) => idx !== index));
  };

  const evalTypeOptions = filterAppointmentTypesForPicker(
    appointmentTypes.filter((t) => t.category === AppointmentCategory.evaluation || t.webptShortCode === "IE" || t.name === "Initial Examination"),
    initialEvalAppointmentTypeId,
    organization?.organizationId,
  );

  const caseStudyOptions = caseStudies.map((study) => {
    return {
      value: study.id,
      label: study.name,
      isActive: study.isActive,
    };
  });

  const caseStudyKeywordMatch: { keyword: string; appointmentType: AppointmentType } | null = (() => {
    if (!selectedCaseStudy) return null;
    const study = caseStudies.find((s) => s.id === selectedCaseStudy);
    if (!study) return null;
    const caseStudyLower = study.name.toLowerCase();
    for (const at of appointmentTypes) {
      if (!at.keyword) continue;
      const keywords = Array.isArray(at.keyword) ? at.keyword : [at.keyword];
      for (const kw of keywords) {
        if (caseStudyLower.includes(kw.toLowerCase())) {
          return { keyword: kw, appointmentType: at };
        }
      }
    }
    return null;
  })();

  // Check if patient already exists
  const patientAlreadyExists = selectedWebptPatient ? existingPatients.some((patient) => patient.webpt?.webptPatientId === selectedWebptPatient.webptPatientId) : false;

  // Existing Patient (Plan of Care) sets providers + case study on the POC page,
  // so we skip those inputs here.
  const isExistingPatientSmart = mode === "smart" && selectedNextStep === "planOfCare";
  const providersRequired = !isExistingPatientSmart;
  const caseStudyRequired = !isExistingPatientSmart;

  // Auto-default case study for Existing Patient mode so we can still build the
  // WebPT patient record (user will refine on the POC page).
  useEffect(() => {
    if (isExistingPatientSmart && !selectedCaseStudy && caseStudies.length > 0) {
      setSelectedCaseStudy(caseStudies[0].id);
    }
  }, [isExistingPatientSmart, selectedCaseStudy, caseStudies]);

  // Validation logic
  const formIsDisabled =
    !selectedWebptPatient ||
    !contactMode ||
    !selectedCaseStudy ||
    patientAlreadyExists ||
    (providersRequired && selectedProviders.length === 0) ||
    (mode === "legacy" && selectedNextStep === "waitlist" && (availableDates.length === 0 || !selectedAppointmentType));

  const submitButtonText = selectedNextStep === "examLink" ? "Add Patient & Send Booking Link" : selectedNextStep === "planOfCare" ? "Add Patient & Create Plan of Care" : "Add to Waitlist";

  return (
    <Box
      sx={{
        overflowY: "auto",
        height: "92%",
        paddingBottom: "40px",
        paddingRight: "20px",
      }}
    >
      <HeaderBox title="Add Patient" onClose={onClose} />

      {mode === "legacy" && (
        <Box
          sx={{
            mt: "50px",
            padding: "12px 14px",
            borderRadius: "10px",
            border: `1px solid rgba(252, 213, 84, 0.3)`,
            backgroundColor: "rgba(252, 213, 84, 0.06)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "18px", color: Colors.warning, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.3 }}>
              Legacy Manual Mode
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.info, fontSize: "0.75rem", mt: 0.25, lineHeight: 1.4 }}>
              This mode is deprecated. Use New Patient or Existing Patient flows for a better experience.
            </Typography>
          </Box>
          <Button
            onClick={handleSwitchToSmart}
            size="small"
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#FFFFFF",
              borderColor: "rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": {
                borderColor: "#FFFFFF",
                backgroundColor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            Exit Legacy Mode
          </Button>
        </Box>
      )}

      <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} marginTop={mode === "legacy" ? "20px" : "50px"}>
        <Typography margin={"10px 0 10px 0"} fontWeight={"bold"} fontSize={"1.2rem"}>
          Import from WebPT
        </Typography>
        {mode === "smart" && (
          <Typography
            variant="body2"
            onClick={handleSwitchToLegacy}
            sx={{
              color: Colors.info,
              fontSize: "0.75rem",
              cursor: "pointer",
              textDecoration: "underline",
              textDecorationColor: "rgba(159, 161, 166, 0.3)",
              textUnderlineOffset: "3px",
              "&:hover": {
                color: "rgba(255,255,255,0.85)",
                textDecorationColor: "rgba(255,255,255,0.5)",
              },
            }}
          >
            Use legacy manual mode
          </Typography>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap="10px" mt={2}>
        <Typography variant="body1">
          Patient (Press Enter to search) <span style={{ color: Colors.error }}>*</span>
        </Typography>
        {currentQueryIsInvalid && (
          <Box display="flex" alignItems="center" gap="5px">
            <WarningIcon color="warning" sx={{ fontSize: "1rem" }} />
            <Typography variant="body2" color="#FCD554">
              Please enter a more specific query
            </Typography>
          </Box>
        )}
      </Box>

      {showPatientDetails ? (
        <>
          {selectedWebptPatient && (
            <Box
              sx={{
                padding: "10px",
                background: Colors.textfield,
                borderRadius: "10px",
                color: "#FFFFFF",
                marginTop: "5px",
                position: "relative",
                "&:hover .close-icon": {
                  visibility: "visible",
                },
              }}
            >
              <Typography variant="body1" fontWeight={"bold"} fontSize={"1rem"}>
                {`${selectedWebptPatient.firstName ?? ""} ${selectedWebptPatient.lastName ?? ""}`}
              </Typography>
              <Box
                sx={{
                  color: Colors.info,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "left", sm: "center" },
                  gap: "10px",
                }}
                mt={0.5}
              >
                <Typography variant="body2">{`${phoneNumber || "Loading Phone Number..."}`}</Typography>
                <CircleIcon sx={{ fontSize: "5px", display: { xs: "none", sm: "block" } }} />
                <Typography variant="body2">{`ID: ${selectedWebptPatient.webptPatientId ?? ""}`}</Typography>
                <CircleIcon sx={{ fontSize: "5px", display: { xs: "none", sm: "block" } }} />
                <Typography variant="body2">{`${timezone}`}</Typography>
              </Box>

              <IconButton
                className="close-icon"
                sx={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  color: "#FFF",
                  visibility: "hidden",
                  padding: "5px",
                  zIndex: 1,
                }}
                onClick={handleCloseDetails}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {patientAlreadyExists && (
            <Box
              display="flex"
              alignItems="center"
              gap="8px"
              mt={2}
              sx={{
                padding: "12px 16px",
                backgroundColor: "rgba(255, 193, 7, 0.1)",
                border: "1px solid rgba(255, 193, 7, 0.3)",
                borderRadius: "8px",
              }}
            >
              <WarningIcon sx={{ color: "#FCD554", fontSize: "1.2rem" }} />
              <Typography variant="body2" color="#FCD554" fontWeight={"500"}>
                This patient already exists in the system
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Autocomplete
          options={webptPatientOptions}
          getOptionLabel={(option) => `${option.label.split(" (ID:")[0]}   (ID: ${option.value})`}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{
                display: "flex",
                justifyContent: "space-between",
                background: Colors.bg3,
                color: "#FFF",
                padding: "8px 16px",
              }}
            >
              <span>{option.label.split(" (ID:")[0]}</span>
              <span style={{ color: Colors.info }}>{`ID: ${option.value}`}</span>
            </li>
          )}
          loading={loadingPatients}
          loadingText="Searching..."
          noOptionsText={validPatientSearches.some((search) => currentQuery.includes(search)) ? "No options" : "Press Enter to search"}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search patient and press Enter"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  handleSearch();
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} size="small" disabled={loadingPatients} sx={{ color: "#FFF", mr: "-4px" }}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              }}
            />
          )}
          value={webptPatientOptions.find((option) => option.value === selectedWebptPatient?.webptPatientId) || null}
          onChange={(e, value) => handleWebptPatientSelection(value?.value || 0)}
          onInputChange={(e, value) => handleInputChange(value)}
          fullWidth
          ListboxComponent={(props) => (
            <ul
              {...props}
              style={{
                padding: 0,
                margin: 0,
                background: Colors.bg3,
              }}
            />
          )}
          sx={{
            borderRadius: "10px",
            background: Colors.textfield,
            marginTop: "5px",
            "& .MuiInputBase-input": {
              padding: "0px",
              fontSize: "0.9rem",
              color: "#FFFFFF",
            },
            "& .MuiSvgIcon-root": {
              color: "#FFF",
            },
            "& .MuiAutocomplete-paper": {
              backgroundColor: Colors.bg3,
              margin: 0,
            },
            "& .MuiAutocomplete-listbox": {
              backgroundColor: Colors.bg3,
            },
          }}
        />
      )}

      {/*<Typography variant="body1" mt={3} mb={1}>
        Phone Number
      </Typography>
      <TextField
        variant="outlined"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        placeholder="Enter phone number"
        fullWidth
        sx={{
          borderRadius: "10px",
          background: Colors.textfield,
          "& .MuiInputBase-input": {
            padding: "15px",
            fontSize: "0.9rem",
            color: "#FFFFFF",
          },
        }}
      />*/}

      {phoneNumbers && phoneNumbers.length > 0 && (
        <>
          <Typography variant="body1" mt={3} mb={1}>
            Phone Number <span style={{ color: Colors.error }}>*</span>
          </Typography>

          <Select
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: (() => {
                  const selectedPhone = phoneNumbers.find((p) => p.number === phoneNumber);
                  return selectedPhone?.twilioType === "mobile" ? "#FFFFFF" : Colors.error; // yellow text if not mobile
                })(),
                transition: "color 0.2s ease-in-out",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          >
            {phoneNumbers.map((phoneObj, index) => (
              <MenuItem
                key={index}
                value={phoneObj.number}
                sx={{
                  color: phoneObj.twilioType === "mobile" ? Colors.white : Colors.error, // make dropdown item text yellow too
                }}
              >
                {convertE164ToReadable(phoneObj.number)} {phoneObj.isPrimary ? "(PRIMARY)" : ""} - {webptPhoneTypeStrings[phoneObj.type]} - {phoneObj.twilioType}
              </MenuItem>
            ))}
          </Select>
        </>
      )}

      {selectedWebptPatient && phoneNumbers.find((p) => p.number === phoneNumber)?.twilioType !== "mobile" && (
        <Typography variant="body2" mt={1} color={Colors.error}>
          Warning: Selected phone number is not a mobile number. Text messages may not be delivered.
        </Typography>
      )}

      {selectedWebptPatient && (
        <>
          <Typography variant="body1" mt={3} mb={1}>
            Preferred Name
          </Typography>
          <StyledTextField value={preferredName} onChange={setPreferredName} />
        </>
      )}

      <Tooltip title="This field is required">
        <Typography variant="body1" mt={3} mb={1}>
          AI agent mode of contact <span style={{ color: Colors.error }}>*</span>
        </Typography>
      </Tooltip>

      <FormControl variant="outlined" fullWidth>
        <Box display="flex" gap="10px">
          <Button
            onClick={() => handleContactModeChange("call")}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: contactMode === "call" ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              backgroundColor: "transparent",
              color: contactMode === "call" ? "#fff" : Colors.info,
            }}
          >
            Call only
          </Button>

          <Button
            onClick={() => handleContactModeChange("text")}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: contactMode === "text" ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              backgroundColor: "transparent",
              color: contactMode === "text" ? "#fff" : Colors.info,
            }}
          >
            Text only
          </Button>

          <Button
            onClick={() => handleContactModeChange("both")}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: contactMode === "both" ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              backgroundColor: "transparent",
              color: contactMode === "both" ? "#fff" : Colors.info,
            }}
          >
            Both
          </Button>
        </Box>
      </FormControl>

      <Typography variant="body1" mt={3} mb={1}>
        Language
      </Typography>
      <FormControl variant="outlined" fullWidth>
        <Box display="flex" gap="10px">
          {languageOptions.map((option) => (
            <Button
              key={option.value}
              onClick={() => setLanguage(option.value)}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                borderColor: language === option.value ? Colors.primary : "rgba(255, 255, 255, 0.2)",
                backgroundColor: "transparent",
                color: language === option.value ? "#fff" : Colors.info,
              }}
            >
              {option.label}
            </Button>
          ))}
        </Box>
      </FormControl>

      {/* Next Steps Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="body1" fontWeight={"600"} fontSize={"1.05rem"} mb={1}>
          {mode === "smart" ? "Patient Type" : "Next Steps"}
        </Typography>
        <Typography variant="body2" color={Colors.info} mb={2.5} fontSize={"0.85rem"}>
          {mode === "smart" ? "Choose how to schedule this patient" : "Choose what happens after adding this patient"}
        </Typography>

        {/* Horizontal Options */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
          }}
        >
          {/* Legacy-only: Add to Waitlist */}
          {mode === "legacy" && (
            <Box
              onClick={() => setSelectedNextStep("waitlist")}
              sx={{
                flex: 1,
                borderRadius: "10px",
                border: `2px solid ${selectedNextStep === "waitlist" ? Colors.primary : "rgba(255, 255, 255, 0.12)"}`,
                backgroundColor: selectedNextStep === "waitlist" ? `${Colors.primary}10` : "rgba(255, 255, 255, 0.03)",
                padding: "18px 16px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: selectedNextStep === "waitlist" ? `${Colors.primary}10` : "rgba(255, 255, 255, 0.06)",
                  borderColor: selectedNextStep === "waitlist" ? Colors.primary : "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <Typography
                variant="body1"
                fontWeight={"600"}
                fontSize={"0.95rem"}
                mb={0.5}
                sx={{
                  color: selectedNextStep === "waitlist" ? "#fff" : "rgba(255, 255, 255, 0.9)",
                }}
              >
                Add to Waitlist
              </Typography>
              <Typography variant="body2" fontSize={"0.8rem"} color={Colors.info} sx={{ lineHeight: 1.4 }}>
                Schedule appointments when slots open
              </Typography>
            </Box>
          )}

          {/* New Patient — send booking link */}
          <Box
            onClick={() => setSelectedNextStep("examLink")}
            sx={{
              flex: 1,
              borderRadius: "10px",
              border: `2px solid ${selectedNextStep === "examLink" ? Colors.primary : "rgba(255, 255, 255, 0.12)"}`,
              backgroundColor: selectedNextStep === "examLink" ? `${Colors.primary}10` : "rgba(255, 255, 255, 0.03)",
              padding: "18px 16px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              position: "relative",
              "&:hover": {
                backgroundColor: selectedNextStep === "examLink" ? `${Colors.primary}10` : "rgba(255, 255, 255, 0.06)",
                borderColor: selectedNextStep === "examLink" ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography
                variant="body1"
                fontWeight={"600"}
                fontSize={"0.95rem"}
                sx={{
                  color: selectedNextStep === "examLink" ? "#fff" : "rgba(255, 255, 255, 0.9)",
                }}
              >
                New Patient
              </Typography>
            </Box>
            <Typography variant="body2" fontSize={"0.8rem"} color={Colors.info} sx={{ lineHeight: 1.4 }}>
              Send booking link for initial visit
            </Typography>
          </Box>

          {/* Existing Patient — create plan of care */}
          <Box
            onClick={() => setSelectedNextStep("planOfCare")}
            sx={{
              flex: 1,
              borderRadius: "10px",
              border: `2px solid ${selectedNextStep === "planOfCare" ? Colors.primary : "rgba(255, 255, 255, 0.12)"}`,
              backgroundColor: selectedNextStep === "planOfCare" ? `${Colors.primary}10` : "rgba(255, 255, 255, 0.03)",
              padding: "18px 16px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              position: "relative",
              "&:hover": {
                backgroundColor: selectedNextStep === "planOfCare" ? `${Colors.primary}10` : "rgba(255, 255, 255, 0.06)",
                borderColor: selectedNextStep === "planOfCare" ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography
                variant="body1"
                fontWeight={"600"}
                fontSize={"0.95rem"}
                sx={{
                  color: selectedNextStep === "planOfCare" ? "#fff" : "rgba(255, 255, 255, 0.9)",
                }}
              >
                Existing Patient
              </Typography>
            </Box>
            <Typography variant="body2" fontSize={"0.8rem"} color={Colors.info} sx={{ lineHeight: 1.4 }}>
              Set up plan of care and schedule
            </Typography>
          </Box>
        </Box>
      </Box>

      {selectedNextStep === "examLink" && evalTypeOptions.length > 0 && (
        <FormControl fullWidth>
          <Typography variant="body1" mt={1} mb={1}>
            Initial Eval Appointment Type
          </Typography>
          <Select
            displayEmpty
            value={initialEvalAppointmentTypeId ?? ""}
            onChange={(e) => setInitialEvalAppointmentTypeId((e.target.value as string) || undefined)}
            renderValue={(selected) => {
              if (!selected) {
                return <Typography sx={{ color: Colors.info }}>Default (auto-detect)</Typography>;
              }
              const option = evalTypeOptions.find((type) => type.appointmentTypeId === selected);
              return option ? option.label || option.name : "Unknown Appointment Type";
            }}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
            MenuProps={menuPropsStyles}
          >
            <MenuItem value="">
              <ListItemText primary="Default (auto-detect)" />
            </MenuItem>
            {evalTypeOptions.map((type) => (
              <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                <ListItemText primary={type.label || type.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {caseStudyRequired && caseStudies.length > 0 && (
        <FormControl fullWidth>
          <Typography variant="body1" mt={3} mb={1}>
            Case Study <span style={{ color: Colors.error }}>*</span>
          </Typography>
          <Select
            value={selectedCaseStudy}
            onChange={(e) => setSelectedCaseStudy(Number(e.target.value))}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
          >
            {caseStudyOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.isActive ? <ListItemText primary={option.label} /> : <ListItemText primary={`${option.label} (inactive)`} primaryTypographyProps={{ color: Colors.error }} />}
              </MenuItem>
            ))}
          </Select>
          {selectedCaseStudy && (
            <Typography variant="caption" sx={{ mt: 0.5, color: caseStudyKeywordMatch ? Colors.success : "text.disabled" }}>
              {caseStudyKeywordMatch ? `Detected: ${caseStudyKeywordMatch.appointmentType.label || caseStudyKeywordMatch.appointmentType.name}` : "No keywords detected"}
            </Typography>
          )}
        </FormControl>
      )}

      {providersRequired && providerGroupOptions.length > 0 && (
        <FormControl fullWidth>
          <Box display="flex" alignItems="center" gap="10px" mt={2}>
            <Typography variant="body1">Provider Group</Typography>
          </Box>
          <Select
            value={providerGroupId || ""}
            onChange={(e) => handleProviderGroupChange(e.target.value)}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
          >
            <MenuItem value="">
              <ListItemText primary="None" />
            </MenuItem>
            {providerGroupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {providersRequired && (
        <FormControl fullWidth>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={2}>
            <Typography variant="body1">
              Providers <span style={{ color: Colors.error }}>*</span>
            </Typography>
            {selectedProviders.length === providerOptions.length ? (
              <Button sx={{ textTransform: "none", fontSize: "0.85rem" }} onClick={() => setSelectedProviders([])}>
                Deselect All
              </Button>
            ) : (
              <Button sx={{ textTransform: "none", fontSize: "0.85rem" }} onClick={() => setSelectedProviders(providerOptions.map((option) => option.value))}>
                Select All
              </Button>
            )}
          </Box>

          <Select
            multiple
            displayEmpty
            value={selectedProviders}
            onChange={(e) => setSelectedProviders(e.target.value as string[])}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography sx={{ color: Colors.info }}>Select options</Typography>;
              }
              return (selected as string[]).map((value) => providerOptions.find((option) => option.value === value)?.label).join(", ");
            }}
            sx={dropdownStyles}
            MenuProps={menuPropsStyles}
          >
            {providerOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedProviders.indexOf(option.value) > -1} sx={{ color: "#FFF" }} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(organization?.patientGroups?.length || 0) > 0 && (
        <FormControl fullWidth sx={{ mt: 3 }}>
          <Typography variant="body1" mb={0.5}>
            Priority Group
          </Typography>
          <Typography variant="body2" sx={{ color: Colors.info, mb: 1 }}>
            Patients in higher-priority groups are offered waitlist slots first.
          </Typography>
          <Select
            value={patientGroupId ?? ""}
            onChange={(e) => setPatientGroupId(e.target.value)}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
          >
            {patientGroupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Waitlist Configuration Fields (legacy only) */}
      {mode === "legacy" && selectedNextStep === "waitlist" && (
        <>
          {appointmentTypes.length > 0 && (
            <FormControl fullWidth sx={{ mt: 3 }}>
              <Typography variant="body1" mb={1}>
                Appointment Types <span style={{ color: Colors.error }}>*</span>
              </Typography>

              <Select
                displayEmpty
                value={selectedAppointmentType}
                onChange={(e) => setSelectedAppointmentType(e.target.value as string)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{ color: Colors.info }}>Select appointment type</Typography>;
                  }
                  const option = appointmentTypes.find((type) => type.appointmentTypeId === selected);
                  return option ? option.name : "Unknown Appointment Type";
                }}
                sx={{
                  borderRadius: "10px",
                  background: Colors.textfield,
                  "& .MuiInputBase-input": {
                    padding: "15px",
                    fontSize: "0.9rem",
                    color: "#FFFFFF",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#FFF",
                  },
                }}
                MenuProps={menuPropsStyles}
              >
                {filterAppointmentTypesForPicker(appointmentTypes, selectedAppointmentType, organization?.organizationId).map((type) => (
                  <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 3,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" mb={1}>
                Max appointments/week
              </Typography>
              <TextField
                type="number"
                value={maxAppointmentsPerWeek}
                onChange={(e) => setMaxAppointmentsPerWeek(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="No limit"
                fullWidth
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#FFF",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    background: Colors.textfield,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "15px",
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" mb={1}>
                Gap between appointments
              </Typography>
              <TextField
                type="number"
                value={gapBetweenAppointments}
                onChange={(e) => setGapBetweenAppointments(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="No gap"
                fullWidth
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#FFF",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    background: Colors.textfield,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "15px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <DatePickerField required dates={availableDates} onDatesChange={setAvailableDates} placeholder="Select available dates" />
          </Box>
        </>
      )}

      <FooterBox onClick={createPatient} disabled={formIsDisabled || loading} buttonText={submitButtonText} loading={loading} sx={{ marginTop: "20px" }} />
    </Box>
  );
};

interface ButtonRowProps {
  selectedValue: string;
  onChange: (value: any) => void;
  options: { label: string; value: string }[];
}

const ButtonRow: React.FC<ButtonRowProps> = ({ selectedValue, onChange, options }) => {
  return (
    <FormControl variant="outlined" fullWidth>
      <Box display="flex" gap="10px">
        {options.map((option) => (
          <Button
            key={option.value}
            onClick={() => onChange(option.value)}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: selectedValue === option.value ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              backgroundColor: "transparent",
              color: selectedValue === option.value ? "#fff" : Colors.info,
            }}
          >
            {option.label}
          </Button>
        ))}
      </Box>
    </FormControl>
  );
};

interface CustomAutocompleteWithLoadingProps {
  label: string;
  options: { label: string; value: string }[];
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  loading: boolean;
}

const CustomAutocompleteWithLoading: React.FC<CustomAutocompleteWithLoadingProps> = ({ label, options, selectedItems, setSelectedItems, loading }) => {
  return (
    <React.Fragment>
      {loading && <LoadingWithMessage message={`Loading ${label.toLowerCase()}...`} size={10} />}
      {!loading && <CustomAutocomplete label={label} options={options} selectedItems={selectedItems} setSelectedItems={setSelectedItems} />}
    </React.Fragment>
  );
};
